import { useCallback, useState } from "react";
import Link from "next/link";
import {
  Button,
  Dialog,
  Flex,
  IconButton,
  Table,
  Text,
} from "@radix-ui/themes";
import { IconBrandInstagram, IconPlugConnected, IconPlugX } from "@tabler/icons-react";
import useUser from "@/lib/useUser";
import { Permission } from "@/services/permissions";
import {
  disconnectInstagramAccount,
  getInstagramAuthStartHref,
  useInstagramAccounts,
  type InstagramAccount,
} from "@/services/instagram";
import { toast } from "sonner";

function DisconnectButton({
  account,
  onDisconnected,
}: {
  account: InstagramAccount;
  onDisconnected: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const label = account.igUsername ? `@${account.igUsername}` : account.igUserId;

  const handleDisconnect = useCallback(async () => {
    setLoading(true);
    try {
      await disconnectInstagramAccount(account.id);
      toast.success(`Disconnected ${label}`);
      setOpen(false);
      onDisconnected();
    } catch (e) {
      console.error(e);
      toast.error("Failed to disconnect");
    } finally {
      setLoading(false);
    }
  }, [account.id, label, onDisconnected]);

  return (
    <>
      <IconButton
        variant="ghost"
        color="red"
        type="button"
        title="Disconnect"
        onClick={() => setOpen(true)}
      >
        <IconPlugX className="icon-accent" />
      </IconButton>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Content maxWidth="400px">
          <Dialog.Title>Disconnect {label}?</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            This account will be removed for your whole agency. Campaign outreach using this
            account will stop until you connect it again.
          </Dialog.Description>
          <Flex gap="2" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" type="button">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              color="red"
              type="button"
              loading={loading}
              onClick={() => void handleDisconnect()}
            >
              Disconnect
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}

type InstagramAccountsManagerProps = {
  /** When true, omit intro copy (parent page provides section heading). */
  compact?: boolean;
};

export default function InstagramAccountsManager({
  compact = false,
}: InstagramAccountsManagerProps) {
  const { hasPermission, permissionsLoading } = useUser();
  const canManageAgency =
    !permissionsLoading && hasPermission(Permission.AGENCY_MANAGE);

  const { data: accounts, error, isLoading, mutate } = useInstagramAccounts(true);

  const handleConnect = () => {
    window.location.href = getInstagramAuthStartHref();
  };

  const accountList = accounts ?? [];
  const hasAccounts = accountList.length > 0;

  return (
    <Flex direction="column" gap="4">
      {!compact && (
        <Text size="2" color="gray" style={{ maxWidth: 640 }}>
          Instagram Business accounts connected here are <strong>shared across your agency</strong>.
          Anyone on outreach can send DMs from any connected account. Connect as many accounts as you
          need (e.g. different brands or regions). Each account must be a{" "}
          <strong>professional (business or creator)</strong> account. If OAuth fails with
          &ldquo;Invalid platform app&rdquo;, your server env likely has the Facebook App ID instead
          of the Instagram App ID from Meta &rarr; Instagram &rarr; Business login settings.
        </Text>
      )}

      {canManageAgency && (
        <Flex direction="row" justify="start">
          <Button type="button" onClick={handleConnect}>
            <IconPlugConnected size={16} style={{ marginRight: 6 }} />
            {hasAccounts ? "Add another account" : "Connect Instagram account"}
          </Button>
        </Flex>
      )}

      {!canManageAgency && !permissionsLoading && (
        <Text size="2" color="gray">
          Only agency admins can connect or disconnect accounts. You can still use connected
          accounts for campaign outreach.
        </Text>
      )}

      {error && (
        <Text size="2" color="red">
          Could not load accounts. Try refreshing the page.
        </Text>
      )}

      {isLoading && <Text size="2" color="gray">Loading accounts…</Text>}

      {!isLoading && !error && !hasAccounts && (
        <Text size="2" color="gray">
          No Instagram accounts connected for your agency yet.
        </Text>
      )}

      {hasAccounts && (
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Account</Table.ColumnHeaderCell>
              {canManageAgency && (
                <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {accountList.map((account) => (
              <Table.Row key={account.id}>
                <Table.Cell>
                  <Flex align="center" gap="2">
                    <IconBrandInstagram size={18} color="var(--purple-9)" />
                    <Flex direction="column" gap="0">
                      <Text size="2" weight="medium">
                        {account.igUsername ? `@${account.igUsername}` : account.igUserId}
                      </Text>
                      <Text size="1" color="gray">
                        Connected {new Date(account.createdAt).toLocaleDateString()}
                      </Text>
                    </Flex>
                  </Flex>
                </Table.Cell>
                {canManageAgency && (
                  <Table.Cell>
                    <DisconnectButton
                      account={account}
                      onDisconnected={() => void mutate()}
                    />
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </Flex>
  );
}

/** Short pointer for campaign settings / outreach when accounts are managed on /me. */
export function InstagramAccountsSettingsLink() {
  return (
    <Text size="2" color="gray">
      Instagram accounts are managed in{" "}
      <Link href="/me" style={{ textDecoration: "underline" }}>
        Settings
      </Link>{" "}
      and shared across your agency.
    </Text>
  );
}
